import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './styles.css';
import { useModalFocusReturn } from '../../hooks/useModalFocusReturn';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

const HandicapAccessibilityModal = ({ isOpen, onClose, location }) => {
  const modalRef = useRef(null);

  // Restore focus to whatever triggered this modal once it closes (declared before
  // the effect below so it captures the trigger element BEFORE that effect moves
  // focus onto the first focusable element inside the modal).
  useModalFocusReturn(isOpen);

  // Lock page scroll while open, without jumping to the top and correctly restoring
  // the exact scroll position on close.
  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const focusableElements = modalRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    const handleEscapeKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleTabKey);
    document.addEventListener('keydown', handleEscapeKey);
    firstElement?.focus({ preventScroll: true });

    return () => {
      document.removeEventListener('keydown', handleTabKey);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Parse the handicap accessibility features
  const getHandicapFeatures = () => {
    const ada = location?.ADA || {};

    // Primary source: `handicapAccessibility` is a single comma-separated string of
    // already human-readable feature labels straight from the API (e.g.
    // "Accommodates Blind/Visually Impaired, ..., Wheelchair Accessibility") — see the
    // identical handling already done for this same field in allProviderFilters.js
    // ("Special handling for handicapAccessibility - it comes as a single
    // comma-separated string"). Defensively also accept an array, in case some
    // environment/record ever sends it pre-split instead of as a single string.
    let summaryFeatures = [];
    if (Array.isArray(ada.handicapAccessibility)) {
      summaryFeatures = ada.handicapAccessibility.map(f => String(f).trim()).filter(f => f.length > 0);
    } else if (typeof ada.handicapAccessibility === 'string' && ada.handicapAccessibility.trim().length > 0) {
      summaryFeatures = ada.handicapAccessibility
        .split(',')
        .map(feature => feature.trim())
        .filter(feature => feature.length > 0);
    }

    // Supplemental source: individual per-feature fields (accommodatesBlindVisuallyImpaired,
    // adjustableExamTable, etc.). These are commonly returned as empty strings by the API
    // even when handicapAccess is 'Y' and handicapAccessibility is fully populated, but the
    // reverse also happens in real-world data: some records only populate a handful of
    // individual flags (e.g. just pubTrans: "YES") without a complete summary string. When
    // the two sources were treated as strict either/or, a partially-populated summary
    // string (or one field's individual flag being the only truthy one) meant every other
    // available attribute was silently dropped — e.g. only "Public Transit Access" showing
    // up even though handicapAccessibility itself listed 14 features. Merging both sources
    // (deduped, case-insensitively) instead of picking just one fixes that. Note some
    // features (e.g. "American Sign Language Interpreter", "Language Interpreter -
    // Language Line") have NO individual field equivalent at all — they can only ever come
    // from the summary string.
    const featureMap = {
      accommodatesBlindVisuallyImpaired: 'Accommodates blind or visually impaired',
      accommodatesDeafnessHardOfHearing: 'Accommodates deaf or hearing impaired',
      accommodatesSpecialNeeds: 'Accommodates special needs',
      adjustableExamTable: 'Adjustable exam table',
      examRoomMedicalEquipment: 'Handicap access: exam room/medical equipment',
      exteriorBuilding: 'Handicap access: exterior',
      interiorBuilding: 'Handicap access: interior',
      restrooms: 'Handicap access: restrooms',
      pubTrans: 'Public transit access',
      parking: 'Wheelchair accessibility'
    };

    const normalize = (str) => str.trim().toLowerCase();
    const seen = new Set(summaryFeatures.map(normalize));
    const combined = [...summaryFeatures];

    Object.entries(featureMap).forEach(([key, label]) => {
      const value = ada[key];
      if (value && value.toString().toUpperCase() === 'YES' && !seen.has(normalize(label))) {
        combined.push(label);
        seen.add(normalize(label));
      }
    });

    return combined;
  };

  const features = getHandicapFeatures();

  // `location.ADA.handicapAccess` is the raw 'Y'/'N' flag from the API (same field
  // providerServiceDetails.jsx compares against for its badge text). The null-data
  // paragraph below explicitly says "the provider says this location IS handicap
  // accessible" — that copy only makes sense when the location is marked accessible
  // ('Y') or the flag is simply missing/unspecified (null/''), and the individual
  // feature list just happens to be empty. If the provider has explicitly marked the
  // location as NOT accessible ('N'), showing that message would contradict the
  // "Not handicap accessible" badge already shown on the page, so it must be
  // suppressed in that case instead.
  const isExplicitlyNotAccessible = String(location?.ADA?.handicapAccess || '').toUpperCase() === 'N';

  return ReactDOM.createPortal(
    <div
      className="handicap-modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="handicap-modal-title"
    >
      <div className="handicap-modal" ref={modalRef}>
        <button
          className="handicap-modal-close"
          onClick={onClose}
          aria-label="Close modal"
          type="button"
        >
          ×
        </button>

        <div className="handicap-modal-content">
          <h2 id="handicap-modal-title" className="handicap-modal-title">
            Accessibility
          </h2>

          <div className="handicap-help-section">
            <h3 className="handicap-subheader">More help with health care visits</h3>
            <p className="handicap-help-text">
              Some people need extra support to talk with their provider. Do you need an interpreter or other help? If your provider doesn't offer this support, call us for help. There is no cost. Your provider can also call before your visit to set things up.
            </p>
            {/* Per requested modal-treatment rule: when accessibility information is
                unavailable (features.length === 0, i.e. null) AND the provider has NOT
                explicitly marked the location as inaccessible, show this business-provided
                informational paragraph directing the user to call the provider directly.
                When accessibility options ARE available, only the header + standard
                paragraph above are shown, and the feature list renders below instead —
                this paragraph is omitted entirely in that case. It is also omitted when
                handicapAccess === 'N', since that copy asserts the location IS accessible,
                which would contradict an explicit "not accessible" designation. This rule
                applies uniformly across all MCS plans/instances; there is no plan-specific
                branching here. */}
            {features.length === 0 && !isExplicitlyNotAccessible && (
              <p className="handicap-help-text">
                The provider says this location is handicap accessible. But we can't find these options for this provider. Need more help? Call them to ask about your needs for health care visits.
              </p>
            )}
          </div>


          <div className="handicap-features-section">
            {features.length > 0 && (
              <ul className="handicap-features-list">
                {features.map((feature, index) => (
                  <li key={index} className="handicap-feature-item">
                    {feature}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default HandicapAccessibilityModal;
